import { ChevronDownIcon } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import { StatusBadge } from '@/components/StatusBadge';
import type { StatusMeta } from '@/components/status-tone';
import { interactiveCellProps } from '@/components/data-table/interactive-cell';
import { SelectContent, SelectItem } from '@/components/ui/select';
import { cn } from '@/lib/utils';

export type StatusSelectProps<S extends string> = {
  value: S;
  onValueChange: (value: S) => void;
  /** The page's status map, the same one its StatusBadges use. */
  statuses: Record<S, StatusMeta>;
  /** Which statuses the menu lists, in order. Default: every key of `statuses`, in its order. */
  options?: readonly S[];
  /** sm for table cells and dense rows, as StatusBadge. */
  size?: 'default' | 'sm';
  disabled?: boolean;
  /** Can't be changed here (no permission, a locked record): a plain StatusBadge. */
  readOnly?: boolean;
  /** Placement only (margins, grid placement); the pill's look comes from StatusBadge. */
  className?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
};

/**
 * An editable status: the StatusBadge itself is the button, with a small chevron, and the menu
 * lists each status as its StatusBadge. Takes the FormField props (id, aria-*) like Select does,
 * and keeps its clicks and keys from reaching a DataTable row (interactiveCellProps).
 */
export function StatusSelect<S extends string>({
  value,
  onValueChange,
  statuses,
  options,
  size = 'default',
  disabled,
  readOnly,
  className,
  ...aria
}: StatusSelectProps<S>) {
  const meta = statuses[value];
  if (readOnly) {
    return <StatusBadge meta={meta} size={size} className={className} />;
  }
  const keys = options ?? (Object.keys(statuses) as S[]);
  return (
    <SelectPrimitive.Root value={value} onValueChange={(v) => onValueChange(v as S)} disabled={disabled}>
      <SelectPrimitive.Trigger
        data-slot="status-select-trigger"
        {...aria}
        {...interactiveCellProps()}
        className={cn(
          'inline-flex w-fit shrink-0 rounded-full outline-none transition-[filter] focus-visible:ring-[3px] focus-visible:ring-ring/50 enabled:hover:brightness-95 data-[state=open]:brightness-95 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-[3px] aria-invalid:ring-destructive/20',
          className
        )}
      >
        <StatusBadge meta={meta} size={size}>
          <SelectPrimitive.Value>{meta?.label ?? value}</SelectPrimitive.Value>
          <ChevronDownIcon aria-hidden className="-mr-0.5 opacity-70" />
        </StatusBadge>
      </SelectPrimitive.Trigger>
      <SelectContent position="popper" align="start">
        {keys.map((key) => (
          <SelectItem key={key} value={key}>
            <StatusBadge meta={statuses[key]} size={size} />
          </SelectItem>
        ))}
      </SelectContent>
    </SelectPrimitive.Root>
  );
}
